import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

const ComparisonContainer = styled.div`
  border-radius: 8px;
  padding: 28px;
  background: #ffffff;
  animation: fadeIn 0.3s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 28px;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #2a3039;
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #666d79;
  margin: 0;
`;

const SelectWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #666d79;
`;

const Select = styled.select`
  border: 1px solid #e1e4e8;
  border-radius: 8px;
  padding: 8px 36px 8px 12px;
  font-size: 14px;
  color: #2a3039;
  background: #ffffff url("data:image/svg+xml,%3Csvg width='12' height='7' viewBox='0 0 12 7' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='%23666D79' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")
    no-repeat right 12px center/12px 7px;
  appearance: none;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #2d70e2;
    box-shadow: 0 0 0 3px rgba(45, 112, 226, 0.1);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BarTrack = styled.div`
  flex: 1;
  background: transparent;
  border-radius: 8px;
  overflow: hidden;
  height: 52px;
`;

const BarFill = styled.div`
  width: ${({ width }) => width}%;
  background: #2d70e2;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 18px;
  color: #fff;
  transition: width 0.3s ease;
  border-radius: 8px;
`;

const BarText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const BarLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BarMeta = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
`;

const BarValue = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
`;

const FooterNote = styled.div`
  font-size: 12px;
  color: #666d79;
  margin-top: 8px;
`;

const Message = styled.div`
  padding: 24px;
  border: 1px dashed #cbd2dc;
  border-radius: 10px;
  background: #f8faff;
  font-size: 14px;
  color: #4f5764;
  line-height: 1.5;
`;

const Comparisons = ({
  question,
  respondents = [],
  groupingOptions = [],
  orgRule = null,
}) => {
  const [selectedGroupingId, setSelectedGroupingId] = useState(
    groupingOptions?.[0]?.id || null
  );
  useEffect(() => {
    if (!groupingOptions || groupingOptions.length === 0) {
      setSelectedGroupingId(null);
      return;
    }

    if (!selectedGroupingId) {
      setSelectedGroupingId(groupingOptions[0].id);
      return;
    }

    const stillValid = groupingOptions.some(
      (option) => option.id === selectedGroupingId
    );

    if (!stillValid) {
      setSelectedGroupingId(groupingOptions[0].id);
    }
  }, [groupingOptions, selectedGroupingId]);

  const comparisonData = useMemo(() => {
    if (!question || question.type !== "quantitative") {
      return [];
    }

    if (!selectedGroupingId) {
      return [];
    }

    const grouping = groupingOptions.find(
      (option) => option.id === selectedGroupingId
    );

    if (!grouping || typeof grouping.getValue !== "function") {
      return [];
    }

    const groupTotals = new Map();

    respondents.forEach((respondent) => {
      const rawResponse = respondent?.responses?.[question.id];

      let value =
        typeof rawResponse === "object" && rawResponse !== null
          ? rawResponse.response
          : rawResponse;

      if (value === undefined || value === null || value === "") {
        return;
      }

      const numericValue =
        typeof value === "number" ? value : parseFloat(value);

      if (!Number.isFinite(numericValue)) {
        return;
      }

      const groupValue = grouping.getValue(respondent);

      if (!groupValue) {
        return;
      }

      const label = String(groupValue).trim();

      if (!label) {
        return;
      }

      if (!groupTotals.has(label)) {
        groupTotals.set(label, { label, total: 0, count: 0 });
      }

      const group = groupTotals.get(label);
      group.total += numericValue;
      group.count += 1;
    });

    // since we are using > instead of <= the logic is inverted from other places.
    const minDataPoints = typeof orgRule === 'number' ? orgRule : 3; // Default to 2 if not a number

    return Array.from(groupTotals.values())
      .map((group) => ({
        ...group,
        average:
          group.count > 0
            ? parseFloat((group.total / group.count).toFixed(2))
            : null,
      }))
      .filter((group) => group.average !== null && group.count > minDataPoints)
      .sort((a, b) => b.average - a.average);
  }, [question, respondents, groupingOptions, selectedGroupingId, orgRule]);

  const scaleMax = useMemo(() => {
    if (question?.scaleRange?.max && Number(question.scaleRange.max) > 0) {
      return Number(question.scaleRange.max);
    }

    const distributionKeys = question?.responses?.distribution
      ? Object.keys(question.responses.distribution)
      : [];

    const numericKeys = distributionKeys
      .map((key) => parseFloat(key))
      .filter((val) => Number.isFinite(val));

    if (numericKeys.length > 0) {
      return Math.max(...numericKeys);
    }

    return 5;
  }, [question]);

  const scaleForWidth = useMemo(() => {
    if (!comparisonData.length) {
      return scaleMax || 5;
    }

    const maxAverage = Math.max(
      ...comparisonData.map((group) => group.average)
    );

    const base = scaleMax > 0 ? scaleMax : 5;

    return Math.max(base, maxAverage);
  }, [comparisonData, scaleMax]);

  const renderContent = () => {
    if (!question || question.type !== "quantitative") {
      return (
        <Message>
          Comparisons are available for quantitative questions with numeric
          responses. Select a different question to view category comparisons.
        </Message>
      );
    }

    if (!groupingOptions?.length) {
      return (
        <Message>
          No category tags are available for comparison. Ensure employee
          categories or departments are configured to see breakdowns.
        </Message>
      );
    }

    if (!comparisonData.length) {
      return (
        <Message>
          We could not compute averages for the selected grouping. Try choosing
          a different category or confirm that responses are recorded.
        </Message>
      );
    }

    return (
      <>
        <Content>
          {comparisonData.map((group) => {
            const width =
              scaleForWidth > 0
                ? Math.max(8, Math.min(100, (group.average / scaleForWidth) * 100))
                : 0;

            return (
              <BarRow key={group.label}>
                <BarTrack>
                  <BarFill width={width}>
                    <BarText>
                      <BarLabel>{group.label}</BarLabel>
                      <BarMeta>
                        {group.count} response{group.count === 1 ? "" : "s"}
                      </BarMeta>
                    </BarText>
                    <BarValue>{group.average.toFixed(1)}</BarValue>
                  </BarFill>
                </BarTrack>
              </BarRow>
            );
          })}
        </Content>
        <FooterNote>Average scores out of {scaleMax}</FooterNote>
      </>
    );
  };

  return (
    <ComparisonContainer>
      <Header>
        <HeaderText>
          <Title>Comparisons</Title>
          <Subtitle>
            Break down of responses by different organizational dimensions
          </Subtitle>
        </HeaderText>
        {groupingOptions?.length > 0 && (
          <SelectWrapper>
            Group by:
            <Select
              value={selectedGroupingId || ""}
              onChange={(event) => setSelectedGroupingId(event.target.value)}
            >
              {groupingOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>
          </SelectWrapper>
        )}
      </Header>
      {renderContent()}
    </ComparisonContainer>
  );
};

export default Comparisons;
